import { RefObjectOrElement, unwrapRefValue, useComposeRef, useOnElementResize } from '@contember/react-utils'
import { ComponentProps, forwardRef, useCallback, useLayoutEffect, useRef } from 'react'
import { uic } from '../utils'

function px<V extends number | false | null | undefined>(value?: V): string {
	return typeof value === 'number' && !(Number.isNaN(value) || value === Infinity || value === -Infinity) ? `${value}px` : ''
}

export const useAutoHeightTextArea = (
	textAreaRef: RefObjectOrElement<HTMLTextAreaElement | null>,
	value: string,
	minRows: number,
	maxRows: number,
) => {
	const measure = useCallback(async (ref: HTMLTextAreaElement | null, minRows: number, maxRows: number) => {
		if (ref) {
			const rowsAttribute = ref.rows

			ref.style.height = ''
			ref.style.minHeight = ''
			ref.style.maxHeight = ''

			if (minRows !== maxRows) {
				const height = ref.scrollHeight - ref.clientHeight + ref.offsetHeight
				let minHeight: number
				let maxHeight: number

				ref.rows = minRows
				minHeight = ref.getBoundingClientRect().height

				if (maxRows === Infinity) {
					maxHeight = Infinity
				} else {
					ref.rows = maxRows
					maxHeight = ref.getBoundingClientRect().height
				}

				ref.style.height = px(height)
				ref.style.minHeight = px(minHeight)
				ref.style.maxHeight = px(maxHeight)
			}

			ref.rows = rowsAttribute
		}
	}, [])

	useOnElementResize(textAreaRef, () => {
		measure(unwrapRefValue(textAreaRef), minRows, maxRows)
	})

	useLayoutEffect(() => {
		measure(unwrapRefValue(textAreaRef), minRows, maxRows)
	}, [maxRows, measure, minRows, textAreaRef, value])
}

export const Textarea = uic('textarea', {
	baseClass:
		'w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
	displayName: 'Textarea',
})

export const TextareaAutosize = forwardRef<
	HTMLTextAreaElement,
	ComponentProps<typeof Textarea> & {
		minRows?: number
		maxRows?: number
	}
>(({ minRows, maxRows, ...props }, ref) => {
	const innerRef = useRef<HTMLTextAreaElement>(null)
	useAutoHeightTextArea(innerRef, props.value?.toString() ?? '', minRows ?? 3, maxRows ?? 100)

	return <Textarea ref={useComposeRef(innerRef, ref)} {...props} />
})
